import React, { useMemo } from 'react';
import QRCode from 'qrcode';


/**
 * Generates a single SVG path data string for all black modules of the QR Code.
 * Each module is drawn as: M x y h 1 v 1 h -1 Z
 */
export function generateQRCodePath(modules, margin = 1) {
  if (!modules || !modules.size || !modules.data) return '';
  const size = modules.size;
  const data = modules.data;
  let path = '';

  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c < size; c += 1) {
      if (data[r * size + c]) {
        path += `M${c + margin} ${r + margin}h1v1h-1Z`;
      }
    }
  }

  return path;
}

/**
 * Format code for human readability below the QR code
 */
export function formatQRText(code) {
  if (!code) return '';
  const str = String(code).trim();
  if (str.length === 32) {
    // Legacy MD5: group in blocks of 8 for clean display
    return str.match(/.{1,8}/g)?.join(' ') || str;
  }
  if (str.length === 13) {
    return `${str.slice(0, 2)} ${str.slice(2, 8)} ${str.slice(8)}`;
  }
  if (str.length > 16) {
    return str.match(/.{1,6}/g)?.join(' ') || str;
  }
  return str;
}

/**
 * Generate a standalone pure SVG string of the QR Code (for print engine)
 */
export function generateQRCodeSVGString(value, size = 64, showText = true) {
  const safeVal = String(value || '').trim() || '000000';
  let qr;
  try {
    qr = QRCode.create(safeVal, { errorCorrectionLevel: 'M' });
  } catch (e) {
    try {
      qr = QRCode.create(safeVal, { errorCorrectionLevel: 'L' });
    } catch (err) {
      return '';
    }
  }

  const margin = 1;
  const totalGridSize = qr.modules.size + margin * 2;
  const pathData = generateQRCodePath(qr.modules, margin);
  const formattedText = formatQRText(safeVal);

  return `
    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%;">
      <svg viewBox="0 0 ${totalGridSize} ${totalGridSize}" style="width: ${size}px; height: ${size}px; shape-rendering: crispEdges; display: block;">
        <path d="${pathData}" fill="#000000" />
      </svg>
      ${
        showText
          ? `<span style="font-family: 'Courier New', Courier, monospace; font-size: 8.5px; letter-spacing: 0.8px; font-weight: 700; color: #111; margin-top: 2px; text-align: center; line-height: 1; word-break: break-all; max-width: 90%;">
              ${formattedText}
            </span>`
          : ''
      }
    </div>
  `;
}

const QRCodeSVG = ({
  value,
  size = 60,
  showText = true,
  className = '',
  style = {},
}) => {
  const safeVal = String(value || '').trim() || '000000';

  const { qr, pathData, totalGridSize } = useMemo(() => {
    let createdQr = null;
    try {
      createdQr = QRCode.create(safeVal, { errorCorrectionLevel: 'M' });
    } catch (e) {
      try {
        createdQr = QRCode.create(safeVal, { errorCorrectionLevel: 'L' });
      } catch (err) {
        createdQr = null;
      }
    }

    if (!createdQr) {
      return { qr: null, pathData: '', totalGridSize: 0 };
    }

    const margin = 1;
    const grid = createdQr.modules.size + margin * 2;
    const path = generateQRCodePath(createdQr.modules, margin);
    return { qr: createdQr, pathData: path, totalGridSize: grid };
  }, [safeVal]);

  const formattedText = useMemo(() => formatQRText(safeVal), [safeVal]);

  if (!qr) return null;

  return (
    <div
      className={`qrcode-svg-container ${className}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        ...style,
      }}
    >
      <svg
        viewBox={`0 0 ${totalGridSize} ${totalGridSize}`}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          shapeRendering: 'crispEdges',
          display: 'block',
        }}
      >
        <path d={pathData} fill="#000000" />
      </svg>
      {showText && (
        <span
          className="product-label-barcode-text"
          style={{
            fontFamily: "'Courier New', Courier, monospace",
            fontSize: safeVal.length > 20 ? '8px' : '9.5px',
            letterSpacing: '0.8px',
            fontWeight: 700,
            color: '#111111',
            marginTop: '2px',
            textAlign: 'center',
            lineHeight: 1,
            wordBreak: 'break-all',
            maxWidth: '92%',
          }}
        >
          {formattedText}
        </span>
      )}
    </div>
  );
};

export default QRCodeSVG;
